<script setup lang="ts">
  import { computed } from 'vue';
  import PosUiIcon from './PosUiIcon.vue';

  type TColor =
    | 'primary'
    | 'neutral'
    | 'success'
    | 'error'
    | 'warning'
    | 'info';
  type TVariant = 'solid' | 'soft' | 'outline';
  type TSize = 'xs' | 'sm' | 'md' | 'lg';

  const props = withDefaults(
    defineProps<{
      label?: string | number;
      color?: TColor;
      variant?: TVariant;
      size?: TSize;
      icon?: string;
    }>(),
    {
      label: undefined,
      color: 'neutral',
      variant: 'soft',
      size: 'sm',
      icon: undefined,
    }
  );

  const classes = computed(() => [
    `color-${props.color}`,
    `variant-${props.variant}`,
    `size-${props.size}`,
  ]);
</script>

<template>
  <span class="pos-ui-badge" :class="classes">
    <PosUiIcon v-if="icon" :name="icon" class="pos-ui-badge-icon" />
    <slot>{{ label }}</slot>
  </span>
</template>

<style scoped lang="scss">
  .pos-ui-badge {
    @apply tw-inline-flex tw-items-center tw-whitespace-nowrap tw-rounded-full tw-font-medium;

    &.size-xs {
      @apply tw-px-1.5 tw-py-0 tw-text-[10px];
    }
    &.size-sm {
      @apply tw-px-2 tw-py-0.5 tw-text-xs;
    }
    &.size-md {
      @apply tw-px-2.5 tw-py-1 tw-text-sm;
    }
    &.size-lg {
      @apply tw-px-3 tw-py-1 tw-text-base;
    }

    // Soft = solid tint + hairline border (crisp over any surface).
    &.color-primary {
      &.variant-solid {
        @apply tw-bg-orange-600 tw-text-on-color;
      }
      &.variant-soft {
        @apply tw-border tw-border-orange-200 tw-bg-orange-50 tw-text-orange-700;
      }
      &.variant-outline {
        @apply tw-border tw-border-orange-600 tw-text-orange-700;
      }
    }

    &.color-neutral {
      &.variant-solid {
        @apply tw-bg-neutral-700 tw-text-on-inverse;
      }
      &.variant-soft {
        @apply tw-border tw-border-neutral-200 tw-bg-neutral-50 tw-text-neutral-600;
      }
      &.variant-outline {
        @apply tw-border tw-border-neutral-300 tw-text-neutral-700;
      }
    }

    &.color-success {
      &.variant-solid {
        @apply tw-bg-emerald-600 tw-text-on-color;
      }
      &.variant-soft {
        @apply tw-border tw-border-emerald-200 tw-bg-emerald-50 tw-text-emerald-700;
      }
      &.variant-outline {
        @apply tw-border tw-border-emerald-600 tw-text-emerald-700;
      }
    }

    &.color-error {
      &.variant-solid {
        @apply tw-bg-red-600 tw-text-on-color;
      }
      &.variant-soft {
        @apply tw-border tw-border-red-200 tw-bg-red-50 tw-text-red-700;
      }
      &.variant-outline {
        @apply tw-border tw-border-red-600 tw-text-red-600;
      }
    }

    &.color-warning {
      &.variant-solid {
        @apply tw-bg-amber-500 tw-text-on-color;
      }
      &.variant-soft {
        @apply tw-border tw-border-amber-200 tw-bg-amber-50 tw-text-amber-700;
      }
      &.variant-outline {
        @apply tw-border tw-border-amber-500 tw-text-amber-700;
      }
    }

    &.color-info {
      &.variant-solid {
        @apply tw-bg-sky-600 tw-text-on-color;
      }
      &.variant-soft {
        @apply tw-border tw-border-sky-200 tw-bg-sky-50 tw-text-sky-700;
      }
      &.variant-outline {
        @apply tw-border tw-border-sky-500 tw-text-sky-700;
      }
    }
  }

  .pos-ui-badge-icon {
    // Margin, not flex gap: old POS hardware (Chrome < 84).
    @apply tw-mr-1 tw-text-[1.2em];
  }
</style>
